import type {
  AuthSession,
  UserProfile,
  OAuthProvider,
  LinkedIdentity,
} from "../shared/authContracts";
import { ProjectApiError } from "./projectApi";
/** Cookie-only contract for the AEE UI. No secrets are returned or stored in JS. */
export function createAuthApi(
  baseUrl: string,
  transport: typeof fetch = fetch,
) {
  const base = baseUrl.replace(/\/$/, "");
  return {
    // Navigate the browser to this URL; OAuth start is not an AJAX call.
    signInUrl(provider: OAuthProvider) {
      return `${base}/auth/${provider}/start`;
    },
    linkProviderUrl(provider: OAuthProvider) {
      return `${base}/auth/link/${provider}/start`;
    },
    async identities(): Promise<{ identities: LinkedIdentity[] }> {
      const response = await transport(`${base}/auth/identities`, {
        credentials: "include",
      });
      if (!response.ok)
        throw new ProjectApiError(
          response.status,
          "IDENTITIES_ERROR",
          "Could not load linked providers.",
        );
      return response.json();
    },
    async unlinkProvider(provider: OAuthProvider): Promise<void> {
      const response = await transport(`${base}/auth/link/${provider}/unlink`, {
        method: "POST",
        credentials: "include",
        headers: { "X-AEE-CSRF": "1" },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new ProjectApiError(
          response.status,
          body?.error?.code ?? "UNLINK_ERROR",
          body?.error?.message ?? "Could not unlink the provider.",
        );
      }
    },
    async saveProfile(profile: UserProfile & {acceptTerms?:boolean;termsVersion?:string;privacyVersion?:string}): Promise<{ profile: UserProfile }> {
      const response = await transport(`${base}/auth/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-AEE-CSRF": "1" },
        body: JSON.stringify(profile),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new ProjectApiError(
          response.status,
          body?.error?.code ?? "PROFILE_ERROR",
          body?.error?.message ?? "Could not save your profile.",
        );
      }
      return response.json();
    },
    async session(): Promise<AuthSession> {
      const response = await transport(`${base}/auth/session`, {
        credentials: "include",
      });
      if (!response.ok)
        throw new ProjectApiError(
          response.status,
          "SESSION_ERROR",
          "Could not load the session.",
        );
      return response.json();
    },
    async logout(): Promise<void> {
      const response = await transport(`${base}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "X-AEE-CSRF": "1" },
      });
      if (!response.ok)
        throw new ProjectApiError(
          response.status,
          "LOGOUT_ERROR",
          "Could not sign out.",
        );
    },
    // Pass this to createProjectApi(baseUrl, auth.transport) for authenticated project requests.
    transport(url: RequestInfo | URL, init?: RequestInit) {
      const headers = new Headers(init?.headers);
      if (
        !["GET", "HEAD", "OPTIONS"].includes(
          init?.method?.toUpperCase() ?? "GET",
        )
      )
        headers.set("X-AEE-CSRF", "1");
      return transport(url, { ...init, headers, credentials: "include" });
    },
  };
}
